import * as React from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { TextInput, type TextInputProps } from '../primitives/TextInput';
import { FormField } from '../patterns/FormField';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';
import { useCanonicalNumericField, type EditPrecisionConfig } from './editPrecision';
import { withInputAddons, type InputAddonProps } from './inputAddons';

interface RhfInputTextFieldProps extends Omit<TextInputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'error'>, InputAddonProps, RhfLayoutProps, RhfFormFieldShellProps {
  field: ControllerRenderProps<FieldValues, string>;
  controlId: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  errorText?: string;
  formatter?: (value: string, prevValue?: string) => string;
  editPrecision?: EditPrecisionConfig;
  inputRef?: React.Ref<HTMLInputElement>;
}

function RhfInputTextField({
  field,
  controlId,
  label,
  hint,
  tooltip,
  required = false,
  semanticId,
  disabled,
  errorText,
  formatter,
  controlWidth,
  editPrecision,
  inputRef,
  startAddon,
  endAddon,
  ...rest
}: RhfInputTextFieldProps): JSX.Element {
  const canonical = useCanonicalNumericField(
    field.value,
    field.onChange,
    editPrecision ?? { storagePrecision: 0, displayPrecision: 0 },
  );
  const useCanonical = editPrecision != null;
  const value = useCanonical ? canonical.displayValue : (field.value ?? '');
  const onChange = useCanonical
    ? (e: React.ChangeEvent<HTMLInputElement>) =>
        canonical.onChange(e.target.value, e.target.selectionStart ?? undefined)
    : formatter
      ? (e: React.ChangeEvent<HTMLInputElement>) => field.onChange(formatter(e.target.value))
      : field.onChange;
  const onBlur = useCanonical
    ? () => {
        canonical.onBlur();
        field.onBlur();
      }
    : field.onBlur;
  const onFocus = useCanonical ? canonical.onFocus : rest.onFocus;

  const input = (
    <TextInput
      ref={composeRefs(field.ref, inputRef, useCanonical ? canonical.inputRef : null)}
      id={controlId}
      name={field.name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      error={Boolean(errorText)}
      semanticId={semanticId}
      {...rest}
    />
  );

  return (
    <FormField
      label={label}
      htmlFor={controlId}
      hint={hint}
      tooltip={tooltip}
      required={required}
      semanticId={semanticId}
      disabled={disabled}
      error={errorText}
      controlWidth={controlWidth}
    >
      {withInputAddons(input, {
        startAddon,
        endAddon,
        error: Boolean(errorText),
        disabled,
      })}
    </FormField>
  );
}

export default RhfInputTextField;
